// Dr.Rx™ System Exception & Diagnostic Logs Widget
import React, { useState, useEffect } from 'react';
import { getErrorLogs, clearErrorLogs, ErrorLog } from '../../services/adminService';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';

const TrashIcon: React.FC = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
);

const ErrorLogsWidget: React.FC = () => {
    const { uid } = useAuth();
    const { showToast, confirmAction } = useToast();
    const [logs, setLogs] = useState<ErrorLog[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<React.ReactNode | null>(null);
    const [selectedLog, setSelectedLog] = useState<ErrorLog | null>(null);

    const fetchLogs = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const fetchedLogs = await getErrorLogs();
            setLogs(fetchedLogs);
        } catch (err: any) {
            console.error(err);
            let errorMessage: React.ReactNode;
            switch (err.code) {
                case 'functions/permission-denied':
                    errorMessage = 'Permission Denied: Your account does not have the required privileges to view error logs.';
                    break;
                case 'functions/internal':
                    errorMessage = 'Server Error: The backend service failed while reading system_logs.';
                    break;
                default:
                    errorMessage = err.message || 'An unexpected error occurred while loading logs.';
            }
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (uid) {
            fetchLogs();
        }
    }, [uid]);

    const handleClearLogs = async () => {
        const confirmed = await confirmAction({
            title: 'Purge All System Error Logs',
            message: 'Are you sure you want to permanently delete all recent system error logs? This action cannot be undone.',
            confirmLabel: 'Purge Logs',
            variant: 'danger'
        });
        if (!confirmed) return;

        try {
            await clearErrorLogs();
            setLogs([]);
            showToast('System error logs cleared.', 'success');
        } catch (err: any) {
            showToast(`Failed to clear logs: ${err.message}`, 'error');
        }
    };

    return (
        <>
            <div className="bg-[#15243E] rounded-2xl border border-blue-900/50 h-full flex flex-col">
                <div className="p-6 border-b border-blue-900/50 flex justify-between items-center">
                    <div>
                        <h3 className="font-bold text-white text-lg">Recent System Error Logs</h3>
                        <p className="text-xs text-gray-400 mt-0.5 tabular-nums">{logs.length} log entries recorded</p>
                    </div>
                    <button
                        onClick={handleClearLogs}
                        disabled={logs.length === 0 || isLoading}
                        className="flex items-center text-xs bg-red-800/60 hover:bg-red-700/80 text-red-200 font-bold py-2 px-3.5 rounded-xl transition-colors disabled:opacity-50"
                    >
                        <TrashIcon /> Clear All
                    </button>
                </div>
                <div className="overflow-y-auto flex-grow custom-scrollbar">
                    {isLoading ? (
                        <div className="p-8 text-center text-gray-400">Loading system logs...</div>
                    ) : error ? (
                        <div className="p-8 text-center text-red-400">{error}</div>
                    ) : logs.length > 0 ? (
                        <div className="divide-y divide-blue-900/50">
                            {logs.map(log => (
                                <div
                                    key={log.id}
                                    onClick={() => setSelectedLog(log)}
                                    className="p-4 hover:bg-white/5 cursor-pointer transition-colors"
                                >
                                    <div className="flex justify-between items-center text-xs text-gray-400 mb-1">
                                        <span className="font-semibold text-red-400 bg-red-500/10 px-2 py-0.5 rounded font-mono">
                                            {log.source}
                                        </span>
                                        <span className="font-mono tabular-nums">{new Date(log.timestamp).toLocaleString()}</span>
                                    </div>
                                    <p className="font-semibold text-white truncate text-sm">{log.message}</p>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="p-12 text-center text-gray-500">No recent errors logged.</div>
                    )}
                </div>
            </div>

            {selectedLog && (
                <div
                    onClick={() => setSelectedLog(null)}
                    className="fixed inset-0 bg-black/75 backdrop-blur-md flex items-center justify-center p-4 z-50"
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        className="bg-gradient-to-br from-[#1E2E4A] to-[#15243E] rounded-2xl border border-blue-900/60 w-full max-w-2xl text-gray-300 p-6 shadow-2xl relative max-h-[80vh] flex flex-col"
                    >
                        <h4 className="font-bold text-white text-lg mb-2">Log Entry Details</h4>
                        <div className="text-xs space-y-1 text-gray-400 mb-4 border-b border-blue-900/50 pb-3 font-mono">
                            <p>
                                <strong>Timestamp:</strong> {new Date(selectedLog.timestamp).toLocaleString()}
                            </p>
                            <p>
                                <strong>Source:</strong> {selectedLog.source}
                            </p>
                            <p>
                                <strong>Message:</strong> {selectedLog.message}
                            </p>
                        </div>
                        <h5 className="font-bold text-white text-xs uppercase tracking-wider mb-2">Diagnostic Context</h5>
                        <pre className="bg-black/40 p-4 rounded-xl text-xs text-gray-300 overflow-auto flex-grow custom-scrollbar">
                            {selectedLog.context
                                ? JSON.stringify(JSON.parse(selectedLog.context), null, 2)
                                : 'No additional context available.'}
                        </pre>
                        <div className="mt-6 text-right">
                            <button
                                onClick={() => setSelectedLog(null)}
                                className="bg-yellow-400 text-gray-950 font-bold py-2 px-6 rounded-xl hover:bg-yellow-300"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ErrorLogsWidget;
